// per docs/caiet-uc-spec.md §UC27-02 §UC-ALRT-05 §UC-ALRT-06
// per docs/ui-shape-admin-aggregations-alerts.md §6 (Template and policy), §13
// The policy editor as the wireframe draws it (AdminAlerte.tsx:2972-3227): a page
// at its own address, `Identificare`, `Potrivire` (one condition per row, added
// and removed in place) and `Livrare` (channels as checkboxes, delivery grouping,
// maximum frequency). The operator stays selectable where the wireframe draws a
// plain "=": the audit's B2 requires `regex` and `in` matchers to be authorable.
// The wireframe's deduplication key and window have no counterpart in the
// backend and are not drawn (owner ruling, 2026-09-10). The technical name is
// derived from the name at creation.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudCheckbox, MudIcon, MudTextInput, MudSelect } from '@egov-moldova/mud-react';
import { useToast } from '@simsm/ui-shared';
import { type ApiRefusal, apiClient, apiRefusal, loadFailureKind } from '../../app/api-client';
import { FormField, FormPage, FormRow, FormSection } from '../../components/FormPage/FormPage';
import { formatInterval, type RuleDto } from './AlertRulesPage';
import { technicalName } from './AlertRuleFormPage';
import { SEVERITIES, type PolicyDto } from './NotificationPoliciesPage';
import { ALERT_RULE_KINDS } from '@simsm/shared-types';

type MatcherOp = 'eq' | 'regex' | 'in';

interface Matcher {
  label: string;
  op: MatcherOp;
  value: string;
}

interface ChannelDto {
  id: string;
  name: string;
  displayName: string;
  active: boolean;
}

interface FormState {
  displayName: string;
  matchers: Matcher[];
  channelIds: string[];
  groupBy: string;
  repeatIntervalSeconds: number;
  /** Not drawn by the wireframe; kept as loaded so a save does not reset them. */
  groupWaitSeconds: number;
  groupIntervalSeconds: number;
}

const RULE_KINDS = ALERT_RULE_KINDS;

// The wireframe's delivery groupings (AdminAlerte.tsx:65), on the labels a
// firing actually carries: the rule, the medicine, the storage location.
const GROUPINGS: { key: string; labels: string[] }[] = [
  { key: 'rule', labels: ['alertname'] },
  // The wireframe's "după registrator": one message per Registrar
  // (docs/shape-ops-notification-grouping.md §2.5).
  { key: 'registrar', labels: ['registrar_idno'] },
  { key: 'medicine', labels: ['medicine_code'] },
  { key: 'site', labels: ['site_id'] },
  { key: 'none', labels: [] },
];

// The wireframe's maximum frequencies (AdminAlerte.tsx:73), in seconds.
const RATE_STEPS = [4 * 3600, 24 * 3600, 7 * 24 * 3600];

const EMPTY: FormState = {
  displayName: '',
  matchers: [{ label: 'severity', op: 'eq', value: '' }],
  channelIds: [],
  groupBy: 'alertname',
  repeatIntervalSeconds: 24 * 3600,
  groupWaitSeconds: 30,
  groupIntervalSeconds: 300,
};

function dtoToForm(p: PolicyDto): FormState {
  return {
    displayName: p.displayName,
    matchers: p.matchers.map((m) => ({
      label: m.label,
      op: (m.op === 'regex' || m.op === 'in' ? m.op : 'eq') as MatcherOp,
      value: Array.isArray(m.value) ? m.value.join('|') : m.value,
    })),
    channelIds: p.channelIds,
    groupBy: p.groupBy.join(','),
    repeatIntervalSeconds: p.repeatIntervalSeconds,
    groupWaitSeconds: p.groupWaitSeconds,
    groupIntervalSeconds: p.groupIntervalSeconds,
  };
}

function matchersToPayload(
  matchers: Matcher[],
): Array<{ label: string; op: string; value: string | string[] }> {
  return matchers
    .filter((m) => m.label.trim().length > 0)
    .map((m) => ({
      label: m.label.trim(),
      op: m.op,
      value:
        m.op === 'in'
          ? m.value
              .split('|')
              .map((v) => v.trim())
              .filter(Boolean)
          : m.value,
    }));
}

export function NotificationPolicyFormPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const isNew = !id;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refusal, setRefusal] = useState<ApiRefusal | null>(null);

  const policies = useQuery({
    queryKey: ['notification-policies'],
    queryFn: () =>
      apiClient
        .get<{ policies: PolicyDto[] }>('/admin/notification-policies')
        .then((r) => r.data.policies),
  });
  const channels = useQuery({
    queryKey: ['notification-channels'],
    queryFn: () =>
      apiClient
        .get<{ channels: ChannelDto[] }>('/admin/notification-channels')
        .then((r) => r.data.channels),
  });
  // "Câmpurile disponibile provin din regulile configurate": the label keys a
  // condition can select on are the ones the configured rules emit.
  const rules = useQuery({
    queryKey: ['alert-rules'],
    queryFn: () =>
      apiClient.get<{ rules: RuleDto[] }>('/admin/alert-rules').then((r) => r.data.rules),
  });

  const existing = isNew ? undefined : policies.data?.find((policy) => policy.id === id);

  useEffect(() => {
    if (isNew || !existing) return;
    setForm(dtoToForm(existing));
  }, [isNew, existing]);

  function edit(patch: Partial<FormState>): void {
    setForm((current) => ({ ...current, ...patch }));
    setDirty(true);
  }

  function updateMatcher(index: number, patch: Partial<Matcher>): void {
    edit({ matchers: form.matchers.map((m, i) => (i === index ? { ...m, ...patch } : m)) });
  }

  const labelKeys = useMemo(() => {
    const keys = new Set(['severity', 'rule_kind']);
    for (const rule of rules.data ?? []) for (const key of Object.keys(rule.labels)) keys.add(key);
    for (const m of form.matchers) if (m.label) keys.add(m.label);
    return [...keys];
  }, [rules.data, form.matchers]);

  const keyLabel = (key: string): string =>
    key === 'severity'
      ? t('alerts.column.severity')
      : key === 'rule_kind'
        ? t('alerts.column.type')
        : t('alerts.policies.meta.label', { key });

  // A value is picked from a list where the key has a closed set and the
  // operator compares one value; everything else is typed.
  function valueOptions(m: Matcher): { value: string; label: string }[] | null {
    if (m.op !== 'eq') return null;
    if (m.label === 'severity') {
      return SEVERITIES.map((v) => ({ value: v, label: t(`alerts.severity.${v}`) }));
    }
    if (m.label === 'rule_kind') {
      return RULE_KINDS.map((v) => ({ value: v, label: t(`alerts.kind.${v}`) }));
    }
    return null;
  }

  const save = useMutation({
    mutationFn: (payload: FormState) => {
      const body = {
        displayName: payload.displayName.trim(),
        matchers: matchersToPayload(payload.matchers),
        channelIds: payload.channelIds,
        groupBy: payload.groupBy
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        groupWaitSeconds: payload.groupWaitSeconds,
        groupIntervalSeconds: payload.groupIntervalSeconds,
        repeatIntervalSeconds: payload.repeatIntervalSeconds,
      };
      return isNew
        ? apiClient
            .post<PolicyDto>('/admin/notification-policies', {
              name: technicalName(payload.displayName, Date.now().toString(36).slice(-4)),
              ...body,
            })
            .then((r) => r.data)
        : apiClient
            .patch<PolicyDto>(`/admin/notification-policies/${id}`, body)
            .then((r) => r.data);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['notification-policies'] });
      setDirty(false);
      toast({
        variant: 'success',
        title: isNew ? t('alerts.toast.policy.created') : t('alerts.toast.policy.saved'),
        text: saved.displayName,
        closeLabel: t('common.close'),
      });
      navigate('/system-admin/alert-policies');
    },
    onError: (error: unknown) =>
      setRefusal(
        apiRefusal(
          error,
          isNew ? 'alerts.policies.error.create_failed' : 'alerts.policies.error.save_failed',
        ),
      ),
  });

  const displayNameError = form.displayName.trim() ? null : t('form.field.required');
  const channelsError = form.channelIds.length
    ? null
    : t('alerts.policies.error.channels_required');
  const matchersError = form.matchers.every((m) => m.label.trim() && m.value.trim())
    ? null
    : t('alerts.policies.error.matchers_incomplete');

  function handleSave(): void {
    setSubmitted(true);
    setRefusal(null);
    if (displayNameError || channelsError || matchersError) return;
    save.mutate(form);
  }

  const groupingOptions = [
    ...GROUPINGS.map((g) => ({
      value: g.labels.join(','),
      label: t(`alerts.policies.grouping.${g.key}`),
    })),
    ...(GROUPINGS.some((g) => g.labels.join(',') === form.groupBy)
      ? []
      : [{ value: form.groupBy, label: form.groupBy }]),
  ];
  const rateOptions = [...new Set([...RATE_STEPS, form.repeatIntervalSeconds])]
    .sort((a, b) => a - b)
    .map((seconds) => ({
      value: String(seconds),
      label:
        seconds % 86400 === 0 && seconds > 86400
          ? t('alerts.interval.days', { count: seconds / 86400 })
          : formatInterval(seconds, t),
    }));

  return (
    <FormPage
      breadcrumb={[
        { label: t('alerts.section.policies'), href: '/system-admin/alert-policies' },
        { label: isNew ? t('alerts.policies.new_crumb') : t('alerts.policies.edit_crumb') },
      ]}
      title={isNew ? t('alerts.policies.new') : t('alerts.policies.edit')}
      lede={t('alerts.policies.form.lede')}
      saveLabel={isNew ? t('alerts.policies.action.create') : t('alerts.action.save_changes')}
      isSaving={save.isPending}
      isDirty={dirty}
      isLoading={policies.isLoading}
      loadError={
        policies.isError
          ? loadFailureKind(policies.error)
          : !isNew && policies.data && !existing
            ? 'not_found'
            : null
      }
      refusal={refusal}
      onSave={handleSave}
      onCancel={() => navigate('/system-admin/alert-policies')}
    >
      <FormSection
        title={t('alerts.section.basics')}
        lede={t('alerts.policies.form.identity_lede')}
      >
        <FormField
          label={t('alerts.policies.field.display_name')}
          required
          error={submitted ? displayNameError : undefined}
        >
          <MudTextInput
            size="md"
            value={form.displayName}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              edit({ displayName: e.detail.value })
            }
          />
        </FormField>
      </FormSection>

      <FormSection
        title={t('alerts.policies.form.matching')}
        lede={t('alerts.policies.form.matching_lede')}
      >
        <div className="simsm-alerts__matchers">
          {form.matchers.map((m, index) => {
            const options = valueOptions(m);
            return (
              <div key={index} className="simsm-alerts__matcher-row">
                <MudSelect
                  size="medium"
                  value={m.label}
                  aria-label={t('alerts.policies.matcher.label')}
                  onMudChange={(e: CustomEvent<{ value: string }>) =>
                    updateMatcher(index, { label: e.detail.value, value: '' })
                  }
                >
                  {labelKeys.map((key) => (
                    <option key={key} value={key}>
                      {keyLabel(key)}
                    </option>
                  ))}
                </MudSelect>
                <MudSelect
                  size="medium"
                  value={m.op}
                  aria-label={t('alerts.policies.matcher.op')}
                  onMudChange={(e: CustomEvent<{ value: string }>) =>
                    updateMatcher(index, { op: e.detail.value as MatcherOp, value: '' })
                  }
                >
                  {(['eq', 'regex', 'in'] as const).map((op) => (
                    <option key={op} value={op}>
                      {t(`alerts.policies.op.${op}`)}
                    </option>
                  ))}
                </MudSelect>
                {options ? (
                  <MudSelect
                    size="medium"
                    value={m.value}
                    aria-label={t('alerts.policies.matcher.value')}
                    placeholder={t('alerts.policies.form.value_placeholder')}
                    onMudChange={(e: CustomEvent<{ value: string }>) =>
                      updateMatcher(index, { value: e.detail.value })
                    }
                  >
                    {options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </MudSelect>
                ) : (
                  <MudTextInput
                    size="md"
                    value={m.value}
                    aria-label={t('alerts.policies.matcher.value')}
                    onMudInput={(e: CustomEvent<{ value: string }>) =>
                      updateMatcher(index, { value: e.detail.value })
                    }
                  />
                )}
                <MudButton
                  shape="circular"
                  variant="destructive"
                  appearance="text"
                  size="sm"
                  label={t('alerts.policies.form.condition_remove')}
                  disabled={form.matchers.length === 1}
                  onClick={() => edit({ matchers: form.matchers.filter((_, i) => i !== index) })}
                >
                  <MudIcon slot="icon" name="cross-small" size={16} />
                </MudButton>
              </div>
            );
          })}
          <MudButton
            shape="circular"
            variant="strict"
            appearance="text"
            size="sm"
            onClick={() =>
              edit({ matchers: [...form.matchers, { label: 'rule_kind', op: 'eq', value: '' }] })
            }
          >
            <MudIcon slot="icon-start" name="plus-small" size={16} />
            {t('alerts.policies.form.add_condition')}
          </MudButton>
          <span className="simsm-alerts__meta">
            {submitted && matchersError ? matchersError : t('alerts.policies.form.matching_hint')}
          </span>
        </div>
      </FormSection>

      <FormSection
        title={t('alerts.policies.form.delivery')}
        lede={t('alerts.policies.form.delivery_lede')}
      >
        <fieldset className="simsm-form-page__fieldset">
          <legend className="simsm-form-page__field-label">
            {t('alerts.policies.field.channels')}
            <span className="simsm-form-page__field-req" aria-hidden="true">
              {' '}
              *
            </span>
          </legend>
          <div className="simsm-alerts__channel-checks">
            {(channels.data ?? []).map((channel) => (
              <MudCheckbox
                key={channel.id}
                size="md"
                checked={form.channelIds.includes(channel.id)}
                disabled={!channel.active && !form.channelIds.includes(channel.id)}
                onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                  edit({
                    channelIds: e.detail.checked
                      ? [...form.channelIds, channel.id]
                      : form.channelIds.filter((x) => x !== channel.id),
                  })
                }
              >
                <span slot="label">{channel.displayName || channel.name}</span>
              </MudCheckbox>
            ))}
          </div>
          <span className="simsm-alerts__meta">
            {submitted && channelsError ? channelsError : t('alerts.policies.form.channels_hint')}
          </span>
        </fieldset>
        <FormRow columns={2}>
          <FormField
            label={t('alerts.policies.form.grouping')}
            required
            hint={t('alerts.policies.form.grouping_hint')}
          >
            <MudSelect
              size="medium"
              value={form.groupBy}
              onMudChange={(e: CustomEvent<{ value: string }>) => edit({ groupBy: e.detail.value })}
            >
              {groupingOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </MudSelect>
          </FormField>
          <FormField
            label={t('alerts.policies.form.rate')}
            required
            hint={t('alerts.policies.form.rate_hint')}
          >
            <MudSelect
              size="medium"
              value={String(form.repeatIntervalSeconds)}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                edit({ repeatIntervalSeconds: Number(e.detail.value) })
              }
            >
              {rateOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </MudSelect>
          </FormField>
        </FormRow>
      </FormSection>
    </FormPage>
  );
}
